// src/v2/components/modules/UploadingSchedule/UploadingScheduleModule.js
import React from "react";
import SaveIcon from '@mui/icons-material/Save';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

// Hooks
import { useFilterManager } from "../../../hooks/useFilterManager";
import { useUploadingSchedule } from "../../../hooks/useUploadingSchedule";
import { useShiftSchedules } from "../../../hooks/useShiftSchedules";

// Shared Components
import { DynamicFilterBar } from "../../filters/DynamicFilterBar";
import { Button, SearchBar } from "../../../../common/APICommon";
import { LoadingSpinner } from "../../../../common/Loading";

// Sub-components
import UploadingScheduleTable from "./components/UploadingScheduleTable";
import UploadingScheduleLegend from "./components/UploadingScheduleLegend";

const UploadingScheduleModule = ({ showGeneralMessage }) => {
  // 1. Filter Management
  const {
    activeFilterState,
    onFilterChange,
    apiFilterPayload,
    applyFilters,
    resetFilters
  } = useFilterManager(['PAYTYPE', 'PAYROLLPERIOD', 'CLASSIFICATION']);

  // 2. Shifts Hook
  const { allShifts, loading: loadingShifts } = useShiftSchedules(true);

  // 3. Main Data Hook
  const {
    loading: loadingData,
    data,
    dateRange,
    editingRowId,
    setEditingRowId,
    handleSearch,
    handleCellChange,
    saveChanges
  } = useUploadingSchedule({ showGeneralMessage, apiFilterPayload });

  const isGlobalLoading = loadingShifts || loadingData;

  return (
    <div className="relative">
      <LoadingSpinner loading={isGlobalLoading} />

      {/* ─── HEADER ─── */}
      <div className="w-full py-3 px-4 border-b bg-white shadow-sm flex justify-between items-center">
        <div>
          <div className="text-sm">
            <span className="text-gray-400">{"Transaction > "}</span>
            <span className="border-b border-black text-gray-700">{"Uploading Schedule"}</span>
          </div>
          <div className="flex items-center gap-2 mt-5">
            <h3 className="text-xl text-customMainColor uppercase tracking-tight m-0">
              Uploading Schedule
            </h3>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4 bg-gray-50/30 min-h-screen">
        {/* ─── FILTER SECTION ─── */}
        <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm" style={{ border: "1px solid #d7d7d7" }}>
          <span className="text-lg font-semibold text-gray-600 block mb-4">Filters</span>

          <DynamicFilterBar
            requestedFilters={['PAYTYPE', 'PAYROLLPERIOD', 'CLASSIFICATION']}
            activeFilterState={activeFilterState}
            onFilterChange={onFilterChange}
            showButtons={false}
          />

          <div className="flex gap-2 mt-6 pt-4 border-t border-gray-100">
            <Button
              name="GENERATE"
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-2.5 border-none shadow-md cursor-pointer font-bold rounded transition-all text-xs"
              onClick={applyFilters}
            />
            <Button
              name="SAVE ALL"
              icon={<SaveIcon fontSize="small" />}
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-2.5 border-none shadow-md cursor-pointer font-bold rounded transition-all text-xs"
              onClick={saveChanges}
            />
            <Button
              name="CLEAR"
              className="bg-gray-400 hover:bg-gray-500 text-white px-6 py-2.5 border-none shadow-md cursor-pointer rounded transition-all text-xs"
              onClick={resetFilters}
            />
          </div>
        </div>

        {/* ─── SEARCH ─── */}
        <SearchBar handleFilter={handleSearch} />

        {/* ─── TABLE ─── */}
        <UploadingScheduleTable
          data={data}
          dateRange={dateRange}
          allShifts={allShifts}
          editingRowId={editingRowId}
          setEditingRowId={setEditingRowId}
          handleCellChange={handleCellChange}
          apiFilterPayload={apiFilterPayload}
        />

        {/* ─── LEGEND ─── */}
        <UploadingScheduleLegend
          data={data}
          allShifts={allShifts}
        />
      </div>
    </div>
  );
};

export default UploadingScheduleModule;